import React, { useEffect, useMemo, useRef, useState } from 'react'
import apiService from '../../services/api.service'
import idValueService from '../../services/api-id-value-service'
import storage from '../../services/stoage.service'
import utils from '../../utils'
import { TiposSepulturasRelatorioForm } from '../../pages/tiposSepulturasRelatorio/form/TiposSepulturasRelatorioForm'
import {
  setFilterLocalizacaoGrid,
  setOpcoesPesquisa,
} from '../../pages/tiposSepulturasRelatorio/tipossepulturasrelatorio.reducer'
import { nomeDoItem } from './padraoNaTela'
import { carregarUnidadesDoUsuario } from './telaFixaAgendavel'

// Agendamento da tela fixa "Tipos de Sepulturas" (PHOENIX-106, fase 2). A tela tem uma unidade, um tipo de sepultura e
// filtros de localizacao em cascata: cada escolha pede ao back o filtro seguinte. O editor do dialogo refaz essa
// cascata com as mesmas funcoes de estado da tela (o reducer dela), guardando o estado aqui em vez de no redux.

const PATH_OPCOES_PESQUISA = '/lottustech/localizacoes/opcoespesquisatelacinema'
// a cascata tem poucos niveis (quadra, lote, ...): o teto so evita laco se o back repetir o mesmo filtro
const MAXIMO_NIVEIS = 12

/** Os filtros de localizacao escolhidos, como o "Visualizar" da tela manda: [{ key, value }]. */
export const filtrosLocalizacaoDosSelecionados = (selecionados) =>
  (selecionados || [])
    .filter((filtro) => filtro && filtro.campoPesquisa && filtro.valorSelecionado)
    .map((filtro) => ({ key: filtro.campoPesquisa.key, value: filtro.valorSelecionado.value }))

/** Os que ja tem valor, que e o que o back recebe para devolver o filtro seguinte. */
const filtrosPreenchidos = (selecionados) =>
  filtrosLocalizacaoDosSelecionados(selecionados).filter((filtro) => filtro.value)

/**
 * O que o "Visualizar" da tela manda, a partir do registro dela e dos filtros de localizacao escolhidos (que a tela
 * guarda fora do registro e entrega em record.selectedFilters). Unidade e tipo vao tambem pelo nome (unidades,
 * tiposSepulturas): o back confere a unidade por ai, e e o que aparece ao reabrir o agendamento e no e-mail.
 */
export const filtrosTiposSepulturasDoRecord = (record = {}) => {
  const unidade = record.unidadeSelecionada || null
  const tipo = record.tipoSepulturaSelecionada || null
  return {
    oidUnidade: unidade ? unidade.id : null,
    oidTipoSepultura: tipo ? tipo.id : null,
    unidades: unidade ? [{ id: unidade.id, value: unidade.value }] : [],
    tiposSepulturas: tipo && tipo.value ? [tipo.value] : [],
    filtrosLocalizacao: filtrosLocalizacaoDosSelecionados(record.selectedFilters),
  }
}

/**
 * O filtro que o back devolveu, com o valor gravado no agendamento ja escolhido (se houver um para ele). Filtro
 * gravado sem valor e o ultimo nivel, que a tela mostrava ainda sem escolha: fica como o back devolveu.
 */
export const comValorGravado = (filtro, gravados) => {
  const gravado = (gravados || []).find((item) => item.key === filtro.campoPesquisa.key)
  if (!gravado || gravado.value === undefined || gravado.value === null) {
    return filtro
  }
  const opcoes = Array.isArray(filtro.campoPesquisa.value) ? filtro.campoPesquisa.value : []
  const opcao = opcoes.find((item) => item.value === gravado.value)
  return { ...filtro, valorSelecionado: opcao || { key: filtro.campoPesquisa.key, value: gravado.value } }
}

/**
 * O tipo de sepultura como o campo "Tipo" precisa receber: a propria opcao da lista. O campo compara o objeto
 * inteiro, e a opcao traz mais do que id e nome, entao um { id, value } montado aqui nao aparece escolhido. Se o tipo
 * nao estiver na lista (ainda carregando, ou sumiu da unidade), fica o { id, value } e o gravado nao se perde.
 */
export const tipoSepulturaDaLista = (opcoes, oidTipoSepultura, nome) => {
  if (!oidTipoSepultura) {
    return ''
  }
  return (opcoes || []).find((opcao) => opcao.id === oidTipoSepultura) || { id: oidTipoSepultura, value: nome }
}

const buscarProximoFiltro = (oidUnidade, oidTipoSepultura, selecionados) =>
  apiService
    .post(`${PATH_OPCOES_PESQUISA}/${oidUnidade}/${oidTipoSepultura}`, filtrosPreenchidos(selecionados))
    .then((resposta) => resposta.data)

/**
 * Refaz a cascata de um agendamento gravado: pede o primeiro filtro, escolhe nele o valor gravado, pede o seguinte,
 * e assim ate acabar o que esta gravado (o ultimo filtro devolvido fica sem valor, para o usuario continuar).
 *
 * @param buscar        (selecionados) => Promise com o proximo filtro, ou vazio
 * @param aoFaltarOpcao (gravado, filtro) => void, opcional (filtro padrao, PHOENIX-107): o valor gravado que nao esta mais nas
 *                      opcoes do nivel nao e escolhido e a cascata para ali (os niveis seguintes dependem dele). Sem
 *                      ele, o valor gravado fica escolhido mesmo fora das opcoes (o agendamento nao perde o que tinha)
 */
export const refazerCascata = async (gravados, buscar, aoFaltarOpcao) => {
  const selecionados = []
  for (let nivel = 0; nivel < MAXIMO_NIVEIS; nivel++) {
    const filtro = await buscar(selecionados)
    const chave = filtro && filtro.campoPesquisa && filtro.campoPesquisa.key
    if (!chave || selecionados.some((item) => item.campoPesquisa.key === chave)) {
      break
    }
    let comValor = comValorGravado(filtro, gravados)
    const opcoes = Array.isArray(filtro.campoPesquisa.value) ? filtro.campoPesquisa.value : []
    if (aoFaltarOpcao && comValor !== filtro && !opcoes.includes(comValor.valorSelecionado)) {
      aoFaltarOpcao(
        (gravados || []).find((item) => item.key === chave),
        filtro
      )
      comValor = filtro
    }
    selecionados.push(comValor)
    if (comValor === filtro) {
      break
    }
  }
  return selecionados
}

/**
 * Filtro padrao (PHOENIX-107, fase 3) da tela de tipos de sepultura: a unidade que ainda existe, o tipo da lista dela
 * (o campo compara o objeto) e a cascata de localizacao refeita com os valores gravados. Os filtros de localizacao
 * ficam fora do registro da tela: vao em extras.selectedFilters.
 *
 * @return Promise<{ record, extras, descartados }>
 */
export const prepararPadraoTiposSepulturas = async (filtros) => {
  const gravados = filtros || {}
  const descartados = []
  const resultado = (record, selectedFilters = []) => ({ record, extras: { selectedFilters }, descartados })
  const empresa = storage.getCompany()
  const gravada = (gravados.unidades || [])[0]
  if (!gravada) {
    return resultado({})
  }
  // as unidades que o usuario acessa, as do seletor da tela; sem a lista nao da para conferir: fica de fora, sem aviso
  const unidades = await carregarUnidadesDoUsuario().catch(() => null)
  const unidade = (unidades || []).find((item) => item.id === gravada.id)
  // a tela tem uma unidade so: sem ela nao ha tipo nem cascata, e aplicar o resto deixaria a tela sem unidade
  if (!unidade) {
    if (!unidades) {
      return resultado({})
    }
    descartados.push(nomeDoItem(gravada))
    return { ...resultado({}), naoAplicado: true }
  }
  const record = { unidadeSelecionada: unidade }
  if (!gravados.oidTipoSepultura) {
    return resultado(record)
  }
  // sem a lista (falha ao carregar) nao da para conferir: o tipo fica de fora, sem aviso
  const tipos = await idValueService
    .getTiposSepulturaV2(unidade.id)
    .then((resposta) => (resposta && resposta.data) || [])
    .catch(() => null)
  const tipo = (tipos || []).find((item) => item.id === gravados.oidTipoSepultura)
  if (!tipo) {
    if (tipos) {
      descartados.push((gravados.tiposSepulturas || [])[0] || String(gravados.oidTipoSepultura))
    }
    return resultado(record)
  }
  record.tipoSepulturaSelecionada = tipo
  const unidadeDaEmpresa = ((empresa && empresa.unidades) || []).find((item) => item.oid === unidade.id)
  if (utils.isTipoSepulturaLocalizacaoGridSemFiltro(empresa, unidadeDaEmpresa, tipo.id)) {
    return resultado(record)
  }
  try {
    const selecionados = await refazerCascata(
      gravados.filtrosLocalizacao,
      (parciais) => buscarProximoFiltro(unidade.id, tipo.id, parciais),
      (gravado, filtro) => gravado && descartados.push(`${filtro.chaveDicionario || gravado.key}: ${gravado.value}`)
    )
    return resultado(record, selecionados)
  } catch (e) {
    // sem a cascata a tela fica com unidade e tipo, como se o usuario tivesse acabado de escolher o tipo
    return resultado(record)
  }
}

/** Editor de filtros do AgendarRelatorioDialog para a tela de tipos de sepultura: o formulario da propria tela. */
export const EditorFiltrosTiposSepulturas = ({ filtros, onChange }) => {
  const [selecionados, setSelecionados] = useState([])
  const selecionadosAtuais = useRef([])
  // a unidade, o tipo e os filtros de localizacao que este editor conhece: os que ele mesmo mandou ao dialogo, ou os
  // gravados com que refez a cascata. Se o dialogo trouxer outros (um agendamento aberto), a cascata e refeita
  const filtrosConhecidos = useRef(null)
  // so a resposta do pedido mais recente vale
  const pedido = useRef(0)
  const empresa = useMemo(() => storage.getCompany(), [])

  const oidUnidade = filtros.oidUnidade || null
  const oidTipoSepultura = filtros.oidTipoSepultura || null
  const unidade = (filtros.unidades || [])[0] || ''
  // os tipos da unidade, a mesma lista que o campo "Tipo" carrega: e de onde sai a opcao escolhida
  const [tipos, setTipos] = useState([])
  useEffect(() => {
    let vigente = true
    setTipos([])
    if (oidUnidade) {
      idValueService
        .getTiposSepulturaV2(oidUnidade)
        .then((resposta) => vigente && setTipos((resposta && resposta.data) || []))
        .catch(() => {})
    }
    return () => {
      vigente = false
    }
  }, [oidUnidade])
  const tipoSepultura = tipoSepulturaDaLista(tipos, oidTipoSepultura, (filtros.tiposSepulturas || [])[0])

  const chaveDosFiltros = (idUnidade, idTipo, localizacao) => JSON.stringify([idUnidade, idTipo, localizacao || []])

  // idUnidade e idTipo vem de quem chama: na troca de unidade ou de tipo os novos ainda nao chegaram em "filtros"
  const atualizar = (novos, idUnidade, idTipo) => {
    const localizacao = filtrosLocalizacaoDosSelecionados(novos)
    selecionadosAtuais.current = novos
    filtrosConhecidos.current = chaveDosFiltros(idUnidade, idTipo, localizacao)
    setSelecionados(novos)
    onChange('filtrosLocalizacao', localizacao)
  }

  // o estado no formato que o reducer da tela espera
  const estadoDaTela = (filtrosSelecionados) => ({
    currentRecord: { unidadeSelecionada: unidade, tipoSepulturaSelecionada: tipoSepultura },
    selectedFilters: filtrosSelecionados,
  })

  const semFiltroDeLocalizacao = (idUnidade, idTipo) => {
    const unidadeDaEmpresa = ((empresa && empresa.unidades) || []).find((item) => item.oid === idUnidade)
    return utils.isTipoSepulturaLocalizacaoGridSemFiltro(empresa, unidadeDaEmpresa, idTipo)
  }

  // pede ao back o filtro seguinte e o acrescenta (ou troca), como a tela faz
  const pedirProximoFiltro = (idUnidade, idTipo) => {
    const numero = ++pedido.current
    buscarProximoFiltro(idUnidade, idTipo, selecionadosAtuais.current)
      .then((filtro) => {
        if (numero === pedido.current && filtro && !utils.isEmptyObject(filtro)) {
          atualizar(
            setOpcoesPesquisa(estadoDaTela(selecionadosAtuais.current), { payload: filtro }).selectedFilters,
            idUnidade,
            idTipo
          )
        }
      })
      .catch(() => {})
  }

  // ao abrir (os filtros chegam depois da primeira renderizacao): refaz a cascata com o que esta gravado. Mudanca
  // que saiu deste editor (escolha do usuario) nao refaz nada: o estado ja esta certo
  const filtrosRecebidos = chaveDosFiltros(oidUnidade, oidTipoSepultura, filtros.filtrosLocalizacao)
  useEffect(() => {
    if (filtrosRecebidos === filtrosConhecidos.current) {
      return
    }
    filtrosConhecidos.current = filtrosRecebidos
    const numero = ++pedido.current
    if (!oidUnidade || !oidTipoSepultura || semFiltroDeLocalizacao(oidUnidade, oidTipoSepultura)) {
      selecionadosAtuais.current = []
      setSelecionados([])
      return
    }
    refazerCascata(filtros.filtrosLocalizacao, (parciais) =>
      buscarProximoFiltro(oidUnidade, oidTipoSepultura, parciais)
    )
      .then((refeitos) => {
        if (numero === pedido.current) {
          selecionadosAtuais.current = refeitos
          setSelecionados(refeitos)
        }
      })
      .catch(() => {})
  }, [filtrosRecebidos]) // eslint-disable-line react-hooks/exhaustive-deps

  const onChangeUnidade = (novaUnidade) => {
    pedido.current++
    onChange('unidades', novaUnidade ? [{ id: novaUnidade.id, value: novaUnidade.value }] : [])
    onChange('oidUnidade', novaUnidade ? novaUnidade.id : null)
    onChange('oidTipoSepultura', null)
    onChange('tiposSepulturas', [])
    atualizar([], novaUnidade ? novaUnidade.id : null, null)
  }

  const onChangeTipoSepultura = (novoTipo) => {
    pedido.current++
    onChange('oidTipoSepultura', novoTipo ? novoTipo.id : null)
    onChange('tiposSepulturas', novoTipo && novoTipo.value ? [novoTipo.value] : [])
    atualizar([], oidUnidade, novoTipo ? novoTipo.id : null)
    if (novoTipo && oidUnidade && !semFiltroDeLocalizacao(oidUnidade, novoTipo.id)) {
      pedirProximoFiltro(oidUnidade, novoTipo.id)
    }
  }

  // escolha num filtro de localizacao: mesma sequencia da tela (setFilterLocalizacaoGrid e depois o proximo filtro,
  // enquanto o ultimo campo de pesquisa da unidade nao tiver sido escolhido)
  const changeHandler = (evento) => {
    const campo = evento.target.name
    const novos = setFilterLocalizacaoGrid(estadoDaTela(selecionadosAtuais.current.slice()), {
      field: campo,
      value: evento.target.value,
    }).selectedFilters
    atualizar(novos, oidUnidade, oidTipoSepultura)
    const ultimoCampo = utils.getUltimoCampoPesquisaLocalizacaoGrid(empresa, oidUnidade, oidTipoSepultura)
    const ultimoEscolhido = novos.find(
      (filtro) => filtro.valorSelecionado && filtro.valorSelecionado.key === ultimoCampo
    )
    if (!ultimoEscolhido) {
      pedirProximoFiltro(oidUnidade, oidTipoSepultura)
    }
  }

  return (
    <div className="p-col-12 agendar-relatorio-filtros-tela">
      <TiposSepulturasRelatorioForm
        record={{ unidadeSelecionada: unidade, tipoSepulturaSelecionada: tipoSepultura }}
        selectedFilters={selecionados}
        changeHandler={changeHandler}
        onChangeUnidade={onChangeUnidade}
        onChangeTipoSepultura={onChangeTipoSepultura}
        disableUnit={false}
        noAgendamento={true}
      />
    </div>
  )
}
